import { useState } from "react";
import {
  Activity,
  BarChart3,
  BookOpen,
  Boxes,
  Building2,
  HardDrive,
  IndianRupee,
  MapPinned,
  MessageSquare,
  ScrollText,
  Settings as SettingsIcon,
  ShieldCheck,
  Ticket as TicketIcon,
  Upload,
  Wrench,
  LayoutDashboard,
  LogOut,
  Moon,
  Sun,
  Users,
} from "lucide-react";
import { useTheme } from "@/app/theme-provider";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/primitives";
import LoginPage from "@/features/auth/LoginPage";
import { useAuth } from "@/features/auth/auth-context";
import UsersPage from "@/features/users/UsersPage";
import RolesPage from "@/features/roles/RolesPage";
import SettingsPage from "@/features/settings/SettingsPage";
import CustomersPage from "@/features/customers/CustomersPage";
import SitesPage from "@/features/sites/SitesPage";
import TicketsPage from "@/features/tickets/TicketsPage";
import EngineerPage from "@/features/engineer/EngineerPage";
import AssetsPage from "@/features/assets/AssetsPage";
import InventoryPage from "@/features/inventory/InventoryPage";
import DashboardPage from "@/features/reports/DashboardPage";
import ReportsPage from "@/features/reports/ReportsPage";
import CallRevenuePage from "@/features/reports/CallRevenuePage";
import KnowledgeBasePage from "@/features/ai/KnowledgeBasePage";
import WhatsappTemplatesPage from "@/features/whatsapp/WhatsappTemplatesPage";
import AuditLogPage from "@/features/audit/AuditLogPage";
import BulkImportPage from "@/features/bulk-import/BulkImportPage";
import { GlobalSearch } from "@/features/search/GlobalSearch";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import PortalDashboard from "@/features/portal/PortalDashboard";
import { useBranding } from "@/features/branding/hooks";
import { COMPANY_LOGO_URL } from "@/features/branding/api";

type View =
  | "dashboard"
  | "myjobs"
  | "tickets"
  | "users"
  | "roles"
  | "sites"
  | "customers"
  | "assets"
  | "inventory"
  | "reports"
  | "call-revenue"
  | "knowledge"
  | "wa-templates"
  | "bulk-import"
  | "audit"
  | "settings";

// A ticket id in the URL (?ticket_id=…, from an email "View ticket" link) deep-
// links straight to that ticket in the Tickets view once signed in.
const deepTicketId =
  typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("ticket_id")
    : null;

/**
 * M2 shell. Still a manual view switch rather than a router — the routed
 * layout with breadcrumbs lands with the dashboard in M12, and introducing
 * routes now would be rewritten then.
 */
export default function App() {
  const { user, isLoading, isAuthenticated, logout, can, canAny } = useAuth();
  const { resolved, setTheme } = useTheme();
  const { data: branding } = useBranding();
  const [view, setView] = useState<View>(deepTicketId ? "tickets" : "dashboard");
  // A filter preset handed to the Tickets view from a dashboard KPI card.
  const [ticketsPreset, setTicketsPreset] = useState<{ preset: string; nonce: number } | null>(
    null,
  );
  const isCustomer = user?.customer_id != null;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <span className="text-sm text-muted-foreground">Loading…</span>
      </div>
    );
  }

  if (!isAuthenticated || !user) return <LoginPage />;

  const nav: { id: View; label: string; icon: typeof Users; visible: boolean }[] = [
    {
      id: "dashboard",
      label: isCustomer ? "Home" : "Dashboard",
      icon: LayoutDashboard,
      visible: true,
    },
    {
      id: "tickets",
      label: isCustomer ? "My requests" : "Tickets",
      icon: TicketIcon,
      visible: can("ticket:read"),
    },
    {
      id: "myjobs",
      label: "My jobs",
      // Field work is an engineer surface; ticket:update is what the
      // check-in and service-report endpoints require.
      icon: Wrench,
      visible: can("ticket:update"),
    },
    { id: "users", label: "Users", icon: Users, visible: can("user:read") },
    { id: "roles", label: "Roles", icon: ShieldCheck, visible: can("role:manage") },
    { id: "sites", label: "Sites", icon: MapPinned, visible: can("site:read") },
    {
      id: "customers",
      label: "Customers",
      icon: Building2,
      visible: can("customer:read"),
    },
    {
      id: "assets",
      label: isCustomer ? "My assets" : "Assets",
      icon: HardDrive,
      visible: can("asset:read"),
    },
    {
      id: "inventory",
      label: "Inventory",
      icon: Boxes,
      visible: can("inventory:read"),
    },
    {
      id: "reports",
      label: "Reports",
      icon: BarChart3,
      visible: can("report:read"),
    },
    {
      id: "call-revenue",
      label: "Call Revenue",
      icon: IndianRupee,
      visible: can("report:revenue:read"),
    },
    {
      id: "bulk-import",
      label: "Bulk upload",
      icon: Upload,
      // Any of the create permissions the bulk importer covers.
      visible: canAny("customer:create", "inventory:manage", "asset:manage"),
    },
    {
      id: "knowledge",
      label: "Knowledge",
      icon: BookOpen,
      visible: can("ticket:read"),
    },
    {
      id: "wa-templates",
      label: "WhatsApp",
      icon: MessageSquare,
      visible: can("settings:manage"),
    },
    {
      id: "audit",
      label: "Audit",
      icon: ScrollText,
      visible: can("audit:read"),
    },
    {
      id: "settings",
      label: "Settings",
      icon: SettingsIcon,
      visible: can("settings:manage"),
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        {/* Two rows: brand + user controls on top, the full menu on its own
            wrapping row below — so every nav item is visible without widening
            the page or scrolling the window. */}
        <div className="container flex flex-col gap-3 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex shrink-0 items-center gap-2">
              {branding?.has_logo ? (
                <img
                  src={COMPANY_LOGO_URL}
                  alt=""
                  className="h-7 w-auto max-w-[160px] object-contain"
                />
              ) : (
                <Activity className="h-5 w-5 text-primary" aria-hidden="true" />
              )}
              <span className="text-lg font-semibold">
                {branding?.name?.trim() || "3Handshake HelpDesk"}
              </span>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <GlobalSearch onNavigate={(target) => setView(target)} />
              <span className="hidden text-sm text-muted-foreground sm:inline">
                {user.full_name}
              </span>
              <NotificationBell />
              <button
                type="button"
                onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
                aria-label={`Switch to ${resolved === "dark" ? "light" : "dark"} theme`}
                className="rounded-md border border-border p-2 hover:bg-accent"
              >
                {resolved === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <Button variant="outline" onClick={() => void logout()}>
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Sign out
              </Button>
            </div>
          </div>

          <nav className="flex flex-wrap items-center gap-1 border-t border-border pt-2" aria-label="Main">
            {nav
              .filter((n) => n.visible)
              .map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => setView(n.id)}
                  aria-current={view === n.id ? "page" : undefined}
                  className={cn(
                    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium",
                    view === n.id
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent",
                  )}
                >
                  <n.icon className="h-4 w-4" aria-hidden="true" />
                  {n.label}
                </button>
              ))}
          </nav>
        </div>
      </header>

      <main className="container py-10">
        {view === "tickets" && (
          <TicketsPage initialDetailId={deepTicketId} initialPreset={ticketsPreset} />
        )}
        {view === "myjobs" && <EngineerPage />}
        {view === "users" && <UsersPage />}
        {view === "roles" && <RolesPage />}
        {view === "sites" && <SitesPage />}
        {view === "customers" && <CustomersPage />}
        {view === "assets" && <AssetsPage />}
        {view === "inventory" && <InventoryPage />}
        {view === "reports" && <ReportsPage />}
        {view === "call-revenue" && <CallRevenuePage />}
        {view === "bulk-import" && <BulkImportPage />}
        {view === "knowledge" && <KnowledgeBasePage />}
        {view === "wa-templates" && <WhatsappTemplatesPage />}
        {view === "audit" && <AuditLogPage />}
        {view === "settings" && <SettingsPage />}
        {view === "dashboard" &&
          (user.customer_id ? (
            <PortalDashboard />
          ) : (
            <DashboardPage
              onNavigate={(target, preset) => {
                if (target === "tickets" && preset) {
                  setTicketsPreset({ preset, nonce: Date.now() });
                }
                setView(target);
              }}
            />
          ))}
      </main>
    </div>
  );
}
